/**
 * V2 API Client — clean wrapper around the PalCare backend endpoints.
 *
 * Replaces direct ApiRequester usage with a simpler, function-based API.
 * Keeps the same endpoints and payload shapes for full backend compatibility.
 *
 * Endpoints (from .env):
 *   VITE_FETCH_ENDPOINT     → /myfetch      (read operations)
 *   VITE_TRANS_ENDPOINT     → /transaction   (write operations)
 *   VITE_REST_ENDPOINT      → /mysave        (table-level CRUD)
 *   VITE_VALID_ENDPOINT     → /myvalidation  (DB validation)
 *   VITE_POST_MQTT          → /mqtt          (MQTT operations)
 *   VITE_MY_REPORTS_ENDPOINT→ /myreport      (report/chart data)
 *   VITE_QUERY_GET          → /getitems      (DynamoDB queries)
 *   VITE_USER_ENDPOINT      → /user_manager  (legacy user management)
 *   VITE_USER_V2_ENDPOINT   → /user          (cloud user management)
 */

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

/** Returns the correct base URL depending on dev mode */
const getBaseUrl = () =>
  import.meta.env.VITE_DEVMODE === 'true'
    ? import.meta.env.VITE_DEVAPI
    : import.meta.env.VITE_API;

import { getAuthHeaders } from './auth';

/** Standard auth headers used by all PalCare backend requests */
const getHeaders = () => getAuthHeaders();

/**
 * Handles HTTP-level errors (503, 500, 403) before JSON parsing.
 * Mirrors the V1 responseErrorHandler from libs.jsx.
 */
async function handleResponse(response) {
  if (response.status === 503) {
    return { Status: 'Error', ErrorCode: 11 };
  }
  if (response.status === 500) {
    return { Status: 'Error', ErrorCode: 10 };
  }
  if (response.status === 403) {
    return { Status: 'Error', ErrorCode: 12 };
  }
  return response.json();
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Fetch data from the backend (read operations).
 * Wraps the V1 "myfetch" endpoint pattern.
 *
 * @param {object} options
 * @param {string} options.procedure  — Stored procedure or "fetchall"/"fetchbyparameter"
 * @param {string} options.reqType    — "stored" | "direct"
 * @param {string} [options.pKey]     — Primary key name (e.g. "campus_id")
 * @param {*}      [options.pVal]     — Primary key value
 * @param {string} [options.table]    — Table name (for direct queries)
 * @param {object} [options.payload]  — Additional payload for stored procedures
 * @param {boolean}[options.readOnly] — Defaults to true
 * @param {number} [options.days]
 * @param {*}      [options.param]
 *
 * @returns {Promise<Array>} The Content array from the response, or []
 */
export async function apiFetch({
  procedure,
  reqType,
  pKey,
  pVal,
  table,
  payload,
  readOnly = true,
  days,
  param,
}) {
  const body = {
    pKey,
    pVal,
    Procedure: procedure,
    RequestType: reqType,
    Days: days,
    Table: table,
    Param: param,
    Payload: payload,
    'read-only': readOnly,
  };

  try {
    const resp = await fetch(getBaseUrl() + import.meta.env.VITE_FETCH_ENDPOINT, {
      method: 'POST',
      headers: await getHeaders(),
      body: JSON.stringify(body),
    });

    const data = await resp.json();

    if (data?.Status === 'OK') {
      return data.Content || [];
    }

    if (data?.Status === 'Error') {
      console.warn('[apiFetch] Backend error — code:', data.ErrorCode);
    }
    return [];
  } catch (err) {
    console.error('[apiFetch] Network or parse error:', err);
    return [];
  }
}

/**
 * Execute a write transaction (create / update / delete).
 * Wraps the V1 "mytransaction" endpoint pattern.
 *
 * @param {object} options
 * @param {string} options.transType   — e.g. "resident_add", "resident_group_edit"
 * @param {Array}  options.payload     — Array of record objects
 * @param {Array}  [options.oldPayload] — Previous values (for edit operations)
 * @param {number} options.campusId
 * @param {string} options.userId
 * @param {string} options.clientId    — Session ID
 * @param {*}      [options.param]
 *
 * @returns {Promise<[boolean, string?]>} [true] on success, [false, errorMsg] on failure
 */
export async function apiTransaction({
  transType,
  payload,
  oldPayload = [],
  campusId,
  userId,
  clientId,
  param,
}) {
  const body = {
    RequestType: transType,
    Content: payload,
    OldContent: oldPayload,
    CampusId: campusId,
    ClientId: clientId,
    Param: param,
    User: userId,
  };

  try {
    const resp = await fetch(getBaseUrl() + import.meta.env.VITE_TRANS_ENDPOINT, {
      method: 'POST',
      headers: await getHeaders(),
      body: JSON.stringify(body),
    });

    const data = await handleResponse(resp);

    if (data?.Status === 'OK') {
      /* Broadcast backend-confirmed writes so cross-cutting listeners can react
         without any per-page wiring — mirrors the `session:updated` CustomEvent
         in services/session.js. Consumed by the interactive help guide to detect
         step completion (docs/INTERACTIVE_HELP_PLAN.md §6.1).

         Fire-and-forget and isolated: a throwing listener must never turn a
         successful write into a failed one, hence the try/catch. */
      try {
        window.dispatchEvent(new CustomEvent('palcare:transaction', { detail: { transType } }));
      } catch {
        /* listener error — never affects the write result */
      }
      return [true];
    }
    if (data?.Status === 'Invalid') {
      return [false, 'Forbidden, user not authorized.'];
    }
    if (data?.Status === '1') {
      return [false, `Error encountered, ${data?.Fail || 0} failed. Please contact support.`];
    }
    if (data?.Status === '2') {
      return [false, `Internal server error. Please contact support.`];
    }
    if (data?.ErrorCode === '9') {
      return [false, 'Record already exists.'];
    }
    return [false, 'Unknown failure, request not processed.'];
  } catch (err) {
    console.error('[apiTransaction] Network or parse error:', err);
    return [false, 'Request failed. Please check your connection and try again.'];
  }
}

/**
 * Legacy user management endpoint (non-cloud campuses).
 * Wraps the V1 "user-manage" endpoint → VITE_USER_ENDPOINT (/user_manager).
 *
 * Body shape: { Content, OldPayload, User }
 *
 * @param {object} options
 * @param {Array}  options.payload     — Action payload array
 * @param {Array}  [options.oldPayload] — Previous values (for edit)
 * @param {string} options.userId      — Current user ID
 * @returns {Promise<[boolean, string?]>}
 */
export async function apiUserManageLegacy({ payload, oldPayload, userId }) {
  const body = {
    Content: payload,
    OldPayload: oldPayload,
    User: userId,
  };

  try {
    const resp = await fetch(getBaseUrl() + import.meta.env.VITE_USER_ENDPOINT, {
      method: 'POST',
      headers: await getHeaders(),
      body: JSON.stringify(body),
    });

    const data = await handleResponse(resp);

    if (data?.Status?.toLowerCase() === 'ok') {
      return [true];
    }
    return [false, `${data?.ErrorCode} ${data?.ErrorReason ? data.ErrorReason : 'Unknown Error'}` || 'Unknown error'];
  } catch (err) {
    console.error('[apiUserManageLegacy] Network or parse error:', err);
    return [false, 'Request failed. Please check your connection and try again.'];
  }
}

/**
 * Cloud user management endpoint (cloud-enabled campuses).
 * Wraps the V1 "user-manage-2" endpoint → VITE_USER_V2_ENDPOINT (/user).
 *
 * Body shape: { Payload, client_id, User }
 *
 * @param {object} options
 * @param {Array}  options.payload  — Action payload array
 * @param {string} options.clientId — Session ID
 * @param {string} options.userId   — Current user ID
 * @returns {Promise<[boolean, string?]>}
 */
export async function apiUserManageCloud({ payload, clientId, userId }) {
  const body = {
    Payload: payload,
    client_id: clientId,
    User: userId,
  };

  try {
    const resp = await fetch(getBaseUrl() + import.meta.env.VITE_USER_V2_ENDPOINT, {
      method: 'POST',
      headers: await getHeaders(),
      body: JSON.stringify(body),
    });

    const data = await handleResponse(resp);

    if (data?.Status?.toLowerCase() === 'ok') {
      return [true];
    }
    return [false, `${data?.ErrorCode} ${data?.ErrorReason ? data.ErrorReason : 'Unknown Error'}` || 'Unknown error'];
  } catch (err) {
    console.error('[apiUserManageCloud] Network or parse error:', err);
    return [false, 'Request failed. Please check your connection and try again.'];
  }
}

/**
 * Table-level CRUD operations (create / update / delete by primary key).
 * Wraps the V1 "mysave" endpoint → VITE_REST_ENDPOINT (/mysave).
 *
 * @param {object} options
 * @param {string} options.table      — Target table name (e.g. "core_config")
 * @param {object} [options.payload]  — New field values (Content)
 * @param {object} [options.oldPayload] — Previous values (OldContent, for edits)
 * @param {string} [options.pKey]     — Primary key column name
 * @param {*}      [options.pVal]     — Primary key value
 * @param {string} [options.condition] — Additional SQL condition
 * @param {number} options.campusId
 * @param {string} options.clientId   — Session ID
 * @param {string} options.userId
 * @param {string} [options.method]   — HTTP method: "PUT" (create), "POST" (update), "DELETE" (default: "POST")
 *
 * @returns {Promise<[boolean, string?]>} [true] on success, [false, errorMsg] on failure
 */
export async function apiSave({
  table,
  payload,
  oldPayload,
  pKey,
  pVal,
  condition,
  campusId,
  clientId,
  userId,
  method = 'POST',
}) {
  const body = {
    Table: table,
    Content: payload,
    OldContent: oldPayload,
    pKey,
    pVal,
    Condition: condition,
    CampusId: campusId,
    ClientId: clientId,
    User: userId,
  };

  try {
    const resp = await fetch(getBaseUrl() + import.meta.env.VITE_REST_ENDPOINT, {
      method,
      headers: await getHeaders(),
      body: JSON.stringify(body),
    });

    const data = await handleResponse(resp);

    if (data?.Status === 'OK') {
      return [true];
    }
    if (data?.Status === 'Invalid') {
      return [false, 'Forbidden, user not authorized.'];
    }
    if (data?.Status === '1') {
      return [false, `Error encountered, ${data?.Fail || 0} failed. Please contact support.`];
    }
    if (data?.Status === '2') {
      return [false, `Internal server error. Please contact support.`];
    }
    if (data?.ErrorCode === '9') {
      return [false, 'Record already exists.'];
    }
    return [false, 'Unknown failure, request not processed.'];
  } catch (err) {
    console.error('[apiSave] Network or parse error:', err);
    return [false, 'Request failed. Please check your connection and try again.'];
  }
}

/**
 * Database validation operations (ping, validate, fix).
 * Wraps the V1 "myvalidation" endpoint → VITE_VALID_ENDPOINT (/myvalidation).
 *
 * @param {object} options
 * @param {string} options.reqType   — "ping" | "validate" | "fix"
 * @param {object} options.payload   — Validation-specific payload
 *
 * @returns {Promise<Array>} The Content array from the response, or []
 */
export async function apiValidation({ reqType, payload }) {
  const body = {
    RequestType: reqType,
    Payload: payload,
  };

  try {
    const resp = await fetch(getBaseUrl() + import.meta.env.VITE_VALID_ENDPOINT, {
      method: 'POST',
      headers: await getHeaders(),
      body: JSON.stringify(body),
    });

    const data = await resp.json();

    if (data?.Status === 'OK') {
      return data.Content || [];
    }

    if (data?.Status === 'Error') {
      console.warn('[apiValidation] Backend error — code:', data.ErrorCode);
    }
    return [];
  } catch (err) {
    console.error('[apiValidation] Network or parse error:', err);
    return [];
  }
}

/**
 * MQTT messaging operations (alert interrogation, device commands).
 * Wraps the V1 "mqtt" endpoint → VITE_POST_MQTT (/mqtt).
 *
 * @param {object} options
 * @param {string} options.operation   — e.g. "getActiveAlert"
 * @param {string} [options.clientId]  — Session ID
 * @param {string} [options.serverId]  — Target server MAC
 * @param {number} [options.campusId]
 * @param {*}      [options.content]   — Message content
 * @param {string} [options.productType]
 * @param {string} [options.msgClass]
 *
 * @returns {Promise<[boolean, string?]>} [true, ""] on success, [false, errorCode] on failure
 */
export async function apiMqtt({
  operation,
  clientId,
  serverId,
  campusId,
  content,
  productType,
  msgClass,
}) {
  const body = {
    Operation: operation,
    ClientId: clientId,
    ServerId: serverId,
    CampusId: campusId,
    Content: content,
    ProductType: productType,
    MsgClass: msgClass,
  };

  try {
    const resp = await fetch(getBaseUrl() + import.meta.env.VITE_POST_MQTT, {
      method: 'POST',
      headers: await getHeaders(),
      body: JSON.stringify(body),
    });

    const data = await resp.json();

    if (data?.Status === 'OK') {
      return [true, ''];
    }
    return [false, data?.ErrorCode || 'Unknown error'];
  } catch (err) {
    console.error('[apiMqtt] Network or parse error:', err);
    return [false, 'Request failed. Please check your connection and try again.'];
  }
}

// ---------------------------------------------------------------------------
// Report / Chart Data
// ---------------------------------------------------------------------------

/**
 * Fetch report or chart data (hourly charts, weekly trends, event summaries).
 * Wraps the V1 "get-mysql-report" endpoint → VITE_MY_REPORTS_ENDPOINT.
 *
 * @param {object} options
 * @param {string} options.repType       — Report type (e.g. "dashboardhourchart", "dashboardweekchart", "eventReasonSummary")
 * @param {number} [options.campusId]
 * @param {string} [options.deviceFilter] — Device ID filter
 * @param {string} [options.startDate]
 * @param {string} [options.endDate]
 * @param {object} [options.filters]     — Additional report-specific filters
 *
 * @returns {Promise<object>} The Res object from the response, or {}
 */
export async function apiReport({ repType, campusId, deviceFilter, startDate, endDate, filters = {} }) {
  const body = {
    campus_id: campusId,
    rep_type: repType,
    start_date: startDate || '',
    end_date: endDate || '',
    device_filter: deviceFilter || '',
    device_category_filter: filters.deviceCategoryFilter || '',
    area_filter: filters.areaFilter || '',
    area_type_filter: filters.areaTypeFilter || '',
    reason_filter: filters.reasonFilter || '',
    user_filter: filters.userFilter || '',
    event_type_filter: filters.eventTypeFilter || '',
    event_category_filter: filters.eventCategoryFilter || '',
    resident_filter: filters.residentFilter || '',
  };

  try {
    const resp = await fetch(getBaseUrl() + import.meta.env.VITE_MY_REPORTS_ENDPOINT, {
      method: 'POST',
      headers: await getHeaders(),
      body: JSON.stringify(body),
    });

    const data = await handleResponse(resp);

    if (data?.Status?.toLowerCase() === 'ok') {
      return data.Res || {};
    }

    if (data?.Status?.toLowerCase() === 'error') {
      console.warn('[apiReport] Backend error — code:', data.ErrorCode);
    }
    return {};
  } catch (err) {
    console.error('[apiReport] Network or parse error:', err);
    return {};
  }
}

// ---------------------------------------------------------------------------
// DynamoDB Single Item (getitem)
// ---------------------------------------------------------------------------

/**
 * Fetch a single item from a DynamoDB table by partition key.
 * Wraps the V1 "getitem" endpoint → VITE_GET_ITEM.
 *
 * @param {object} options
 * @param {string} options.table          — DynamoDB table name (e.g. "CampusConfig")
 * @param {string} options.partitionKey   — Partition key column name
 * @param {string} options.partitionValue — Partition key value
 * @param {string} [options.campusId]     — Campus GUID
 *
 * @returns {Promise<object>} Single item object, or {}
 */
export async function apiDynamoGetItem({ table, partitionKey, partitionValue, campusId }) {
  const body = {
    Table: table,
    PartitionKey: partitionKey,
    PartitionValue: partitionValue,
    CampusId: campusId,
  };

  try {
    const resp = await fetch(getBaseUrl() + import.meta.env.VITE_GET_ITEM, {
      method: 'POST',
      headers: await getHeaders(),
      body: JSON.stringify(body),
    });

    const data = await resp.json();

    if (data?.Status !== 'Error' && data?.Content?.length > 0) {
      return data.Content[0];
    }

    if (data?.Status === 'Error') {
      console.warn('[apiDynamoGetItem] Backend error — code:', data.ErrorCode);
    }
    return {};
  } catch (err) {
    console.error('[apiDynamoGetItem] Network or parse error:', err);
    return {};
  }
}

// ---------------------------------------------------------------------------
// DynamoDB Table Scan (getall)
// ---------------------------------------------------------------------------

/**
 * Scan/fetch all items from a DynamoDB table.
 * Wraps the V1 "getall" endpoint → VITE_GET_ALL.
 *
 * @param {object} options
 * @param {string} options.table       — DynamoDB table name (e.g. "ReasonCode")
 * @param {string} [options.campusId]  — Campus GUID
 * @param {string} [options.search]    — Optional search filter
 * @param {string} [options.projection] — Projection expression
 *
 * @returns {Promise<Array>} Array of items, or []
 */
export async function apiDynamoGetAll({ table, campusId, search, projection }) {
  const body = {
    Table: table,
    Search: search || '',
    CampusId: campusId,
    Projection: projection || '',
  };

  try {
    const resp = await fetch(getBaseUrl() + import.meta.env.VITE_GET_ALL, {
      method: 'POST',
      headers: await getHeaders(),
      body: JSON.stringify(body),
    });

    const data = await resp.json();

    if (data?.Status !== 'Error' && data?.Content?.length > 0) {
      return data.Content[0];
    }

    if (data?.Status === 'Error') {
      console.warn('[apiDynamoGetAll] Backend error — code:', data.ErrorCode);
    }
    return [];
  } catch (err) {
    console.error('[apiDynamoGetAll] Network or parse error:', err);
    return [];
  }
}

// ---------------------------------------------------------------------------
// DynamoDB Save (save)
// ---------------------------------------------------------------------------

/**
 * Save/update a DynamoDB item by partition key.
 * Wraps the V1 "save" endpoint → VITE_POST_ENDPOINT.
 *
 * @param {object} options
 * @param {string} options.table         — DynamoDB table name (e.g. "CampusConfig")
 * @param {string} options.partitionKey  — Partition key column name
 * @param {Array}  options.payload       — Array of record objects to save
 * @param {string} [options.campusId]    — Campus GUID
 * @param {string} [options.sortKey]     — Sort key (if applicable)
 * @param {boolean}[options.overwrite]   — Whether to overwrite existing
 *
 * @returns {Promise<[boolean, string?]>} [true] on success, [false, errorMsg] on failure
 */
export async function apiDynamoSave({ table, partitionKey, payload, campusId, sortKey, overwrite }) {
  const body = {
    Table: table,
    PartitionKey: partitionKey,
    Content: payload,
    SortKey: sortKey,
    CampusId: campusId,
    Overwrite: overwrite,
  };

  try {
    const resp = await fetch(getBaseUrl() + import.meta.env.VITE_POST_ENDPOINT, {
      method: 'POST',
      headers: await getHeaders(),
      body: JSON.stringify(body),
    });

    const data = await resp.json();

    if (data?.Status === 'OK') {
      return [true];
    }
    if (data?.Status === 'Invalid') {
      return [false, 'Forbidden, user not authorized.'];
    }
    if (data?.Status === '1') {
      return [false, `Error encountered, ${data?.Fail || 0} failed. Please contact support.`];
    }
    if (data?.Status === '2') {
      return [false, `Internal server error. Please contact support.`];
    }
    if (data?.ErrorCode === '9') {
      return [false, 'Record already exists.'];
    }
    return [false, 'Unknown failure, request not processed.'];
  } catch (err) {
    console.error('[apiDynamoSave] Network or parse error:', err);
    return [false, 'Request failed. Please check your connection and try again.'];
  }
}

// ---------------------------------------------------------------------------
// Redis / Cached Query (redget)
// ---------------------------------------------------------------------------

/**
 * Query cached / Redis-backed data (e.g. distinct device names).
 * Wraps the V1 "redget" endpoint → VITE_RED_QUERY.
 *
 * @param {object} options
 * @param {string} options.table          — Table name (e.g. "devices")
 * @param {string} [options.column]       — Column to query
 * @param {string} [options.getType]      — Query type (e.g. "distinct")
 * @param {string} [options.search]       — Search filter
 * @param {string} [options.searchKey]    — Search key column
 * @param {string} [options.startValue]   — Range start
 * @param {string} [options.endValue]     — Range end
 * @param {string} [options.selector]     — Selector column
 * @param {string} [options.selectorValue] — Selector value
 * @param {string} [options.sortColumn]   — Sort column
 * @param {string} [options.sortOrder]    — Sort order ("ASC" / "DESC")
 * @param {string} [options.campusId]     — Campus GUID
 *
 * @returns {Promise<Array>} Array of result objects, or []
 */
export async function apiRedQuery({
  table,
  column,
  getType,
  search,
  searchKey,
  startValue,
  endValue,
  selector,
  selectorValue,
  sortColumn,
  sortOrder,
  campusId,
}) {
  const body = {
    Table: table || '',
    Column: column || '',
    getType: getType || '',
    Search: search || '',
    SearchKey: searchKey || '',
    StartValue: startValue || '',
    EndValue: endValue || '',
    Selector: selector || '',
    SelectorValue: selectorValue || '',
    SortColumn: sortColumn || '',
    SortOrder: sortOrder || '',
    CampusId: campusId,
  };

  try {
    const resp = await fetch(getBaseUrl() + import.meta.env.VITE_RED_QUERY, {
      method: 'POST',
      headers: await getHeaders(),
      body: JSON.stringify(body),
    });

    const data = await resp.json();

    if (data?.Status !== 'Error' && data?.Content?.length > 0) {
      return data.Content;
    }
    return [];
  } catch (err) {
    console.error('[apiRedQuery] Network or parse error:', err);
    return [];
  }
}

// ---------------------------------------------------------------------------
// DynamoDB Query (getitems)
// ---------------------------------------------------------------------------

/**
 * Query items from DynamoDB-style tables (debug data, learn-in records, etc.).
 * Wraps the V1 "getitems" endpoint → VITE_QUERY_GET.
 *
 * @param {object} options
 * @param {string} options.table          — DynamoDB table name (e.g. "DebugSerialNumber")
 * @param {string} options.partitionKey   — Partition key column name
 * @param {string} options.partitionValue — Partition key value
 * @param {number} [options.campusId]
 * @param {string} [options.secondIndex]  — Secondary index name
 * @param {string} [options.projection]   — Projection expression
 * @param {string} [options.search]       — Search filter
 * @param {boolean}[options.includeDeleted]
 *
 * @returns {Promise<Array>} Array of items, or []
 */
export async function apiQueryItems({
  table,
  partitionKey,
  partitionValue,
  campusId,
  secondIndex,
  projection,
  search,
  includeDeleted,
}) {
  const body = {
    Table: table,
    PartitionKey: partitionKey,
    PartitionValue: partitionValue,
    CampusId: campusId,
    SecondIndex: secondIndex || '',
    Projection: projection || '',
    Search: search || '',
    IncludeDeleted: includeDeleted || false,
  };

  try {
    const resp = await fetch(getBaseUrl() + import.meta.env.VITE_QUERY_GET, {
      method: 'POST',
      headers: await getHeaders(),
      body: JSON.stringify(body),
    });

    const data = await resp.json();

    if (data?.Status === 'OK' && data?.Content) {
      return data.Content;
    }

    if (data?.Status === 'Error') {
      console.warn('[apiQueryItems] Backend error — code:', data.ErrorCode);
    }
    return [];
  } catch (err) {
    console.error('[apiQueryItems] Network or parse error:', err);
    return [];
  }
}

// ---------------------------------------------------------------------------
// Remote Command (device location / alarm)
// ---------------------------------------------------------------------------

/**
 * Send a remote command to a device (e.g. find location, trigger alarm).
 * Wraps the V1 "remotecommand" endpoint → VITE_REMOTE_COMMAND_ENDPOINT.
 *
 * @param {object} options
 * @param {number} options.campusId  — Campus account ID
 * @param {string} options.userId    — Current user ID
 * @param {string} options.clientId  — Session/client ID
 * @param {object} options.payload   — Command payload, e.g. { devid: "MAC", command: "location" }
 *
 * @returns {Promise<boolean>} true if command accepted (HTTP 200), false otherwise
 */
export async function apiRemoteCommand({ campusId, userId, clientId, payload }) {
  const body = {
    source: 'portal',
    campus_id: campusId,
    user_id: userId,
    payload,
    client_id: clientId,
  };

  try {
    const resp = await fetch(getBaseUrl() + import.meta.env.VITE_REMOTE_COMMAND_ENDPOINT, {
      method: 'POST',
      headers: await getHeaders(),
      body: JSON.stringify(body),
    });

    return resp?.status === 200;
  } catch (err) {
    console.error('[apiRemoteCommand] Network error:', err);
    return false;
  }
}
